import React, { useState, useEffect } from "react";
import { Input, Select } from "antd";
import { useDispatch } from "react-redux";
import { businessSlice } from "../../../../../features/business/businessSlice";
import { useLocation, useNavigate } from "react-router-dom";
import ProtectedPaymentRoute from "../../../../auth/Auth";

const { Option } = Select;

const TITLE_OPTIONS = ["President", "CEO", "Creative Director", "Founder", "Director"];

const EMPTY_MEMBER = {
  firstName: "",
  lastName: "",
  selectedOwnership: "",
  ownershipPercentage: "",
  ssnnumber: "",
};

export default function MemberCorpo() {
  ProtectedPaymentRoute();

  const dispatch = useDispatch();
  const actions = businessSlice.actions;
  const navigate = useNavigate();
  const location = useLocation();

  const normalizeRows = (data) =>
    (data || []).map((row) => ({
      ...row,
      selectedOwnership:
        row.selectedOwnership && typeof row.selectedOwnership === "object"
          ? row.selectedOwnership.label || ""
          : row.selectedOwnership || "",
    }));

  const [rows, setRows] = useState(
    normalizeRows(JSON.parse(localStorage.getItem("businessMembers")))
  );
  const [member, setMember] = useState({ ...EMPTY_MEMBER });
  const [errors, setErrors] = useState({});
  const [modal, setModal] = useState({ open: false, message: "" });

  const remainingPercentage =
    100 - rows.reduce((t, m) => t + parseFloat(m.ownershipPercentage || 0), 0);

  const showAlert = (msg) => setModal({ open: true, message: msg });
  const closeAlert = () => setModal({ open: false, message: "" });

  useEffect(() => {
    localStorage.setItem("businessMembers", JSON.stringify(rows));
  }, [rows]);

  const handleSSNChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").substring(0, 9);
    let fmt = "";
    for (let i = 0; i < raw.length; i++) {
      if (i === 3 || i === 5) fmt += "-";
      fmt += raw[i];
    }
    setMember((p) => ({ ...p, ssnnumber: fmt }));
    if (errors.ssnnumber) setErrors((p) => ({ ...p, ssnnumber: "" }));
  };

  const handleChange = (field, value) => {
    setMember((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validateMember = () => {
    const e = {};
    if (!member.firstName.trim()) e.firstName = "First name is required";
    if (!member.lastName.trim()) e.lastName = "Last name is required";
    if (!member.selectedOwnership) e.selectedOwnership = "Please select a title";
    if (!member.ownershipPercentage) {
      e.ownershipPercentage = "Ownership % is required";
    } else {
      const pct = parseFloat(member.ownershipPercentage);
      if (pct <= 0 || pct > remainingPercentage)
        e.ownershipPercentage = `Max available: ${remainingPercentage}%`;
    }
    if (!member.ssnnumber) {
      e.ssnnumber = "SSN is required";
    } else if (!/^\d{3}-\d{2}-\d{4}$/.test(member.ssnnumber)) {
      e.ssnnumber = "Format must be 000-00-0000";
    } else if (rows.some((r) => r.ssnnumber === member.ssnnumber)) {
      e.ssnnumber = "This SSN is already added";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddMember = (e) => {
    if (e) e.preventDefault();
    if (!validateMember()) return;
    setRows([...rows, { ...member }]);
    setMember({ ...EMPTY_MEMBER });
    setErrors({});
  };

  const handleRemove = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
  };

  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (rows.length === 0) {
      showAlert("Please add at least one member before proceeding.");
      return;
    }
    const payload = rows.map(({ firstName, lastName, ssnnumber, selectedOwnership, ownershipPercentage }) => ({
      firstName,
      lastName,
      ownershipPercentage,
      ssnNumber: ssnnumber,
      ownership: selectedOwnership,
    }));
    dispatch(actions.update_business_members_corpo(payload));
    const edit = new URLSearchParams(location.search).get("edit");
    navigate(edit === "true" ? "/invoice" : "/corp_question/2");
  };

  const handleNextPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleNext(e);
    }
  };

  const isEdit = !!new URLSearchParams(location.search).get("edit");

  return (
    <div className="min-h-[75vh] bg-white py-12 lg:py-20 font-sans flex items-center">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          
          {/* Left Hero Graphic */}
          <div className="lg:col-span-5 flex justify-center items-center">
            <img
              src="Assets/Form_img/people.png"
              alt="Corporate Members illustration"
              className="w-full max-w-[340px] h-auto object-contain drop-shadow-sm"
            />
          </div>

          {/* Right Form Area */}
          <div className="lg:col-span-7">
            
            {/* Header */}
            <div className="mb-8">
              <span className="text-[#1a73e8] font-medium tracking-wider uppercase text-xs mb-2 block">
                Corporate Officers & Shareholders
              </span>
              <h1 className="text-3xl sm:text-4xl font-normal text-[#202124] tracking-tight mb-2">
                Add corporate member details
              </h1>
              <p className="text-[15px] text-[#5f6368] leading-relaxed">
                Add all founding officers, directors, and equity shareholders for your corporation.
              </p>
            </div>

            {/* Inputs Form */}
            <form onKeyDown={handleNextPress} className="space-y-4 mb-6">
              
              {/* First & Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#3c4043] mb-1.5">
                    First Name <span className="text-[#d93025] font-semibold">*</span>
                  </label>
                  <Input
                    id="member-first-name"
                    placeholder="e.g. John"
                    size="large"
                    value={member.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    status={errors.firstName ? "error" : ""}
                    className="rounded-[4px]"
                  />
                  {errors.firstName && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] font-medium text-[#3c4043] mb-1.5">
                    Last Name <span className="text-[#d93025] font-semibold">*</span>
                  </label>
                  <Input
                    id="member-last-name"
                    placeholder="e.g. Doe"
                    size="large"
                    value={member.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    status={errors.lastName ? "error" : ""}
                    className="rounded-[4px]"
                  />
                  {errors.lastName && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              {/* Title & Ownership % */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#3c4043] mb-1.5">
                    Title / Role <span className="text-[#d93025] font-semibold">*</span>
                  </label>
                  <Select
                    id="member-title"
                    placeholder="Select corporate title"
                    size="large"
                    style={{ width: "100%" }}
                    value={member.selectedOwnership || undefined}
                    onChange={(val) => handleChange("selectedOwnership", val)}
                    status={errors.selectedOwnership ? "error" : ""}
                  >
                    {TITLE_OPTIONS.map((t) => (
                      <Option key={t} value={t}>{t}</Option>
                    ))}
                  </Select>
                  {errors.selectedOwnership && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.selectedOwnership}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="block text-[13px] font-medium text-[#3c4043]">
                      Ownership % <span className="text-[#d93025] font-semibold">*</span>
                    </label>
                    <span className="text-xs text-[#1a73e8] font-medium">
                      Remaining: {remainingPercentage}%
                    </span>
                  </div>
                  <Input
                    id="member-ownership"
                    placeholder="e.g. 50"
                    size="large"
                    suffix="%"
                    value={member.ownershipPercentage}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 3);
                      handleChange("ownershipPercentage", v);
                    }}
                    status={errors.ownershipPercentage ? "error" : ""}
                    className="rounded-[4px]"
                  />
                  {errors.ownershipPercentage && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.ownershipPercentage}
                    </p>
                  )}
                </div>
              </div>

              {/* SSN Number */}
              <div>
                <label className="block text-[13px] font-medium text-[#3c4043] mb-1.5">
                  SSN Number <span className="text-[#d93025] font-semibold">*</span>
                </label>
                <Input
                  id="member-ssn"
                  placeholder="000-00-0000"
                  size="large"
                  value={member.ssnnumber}
                  onChange={handleSSNChange}
                  status={errors.ssnnumber ? "error" : ""}
                  maxLength={11}
                  className="rounded-[4px]"
                />
                {errors.ssnnumber && (
                  <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                    <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                    {errors.ssnnumber}
                  </p>
                )}
              </div>

              {/* Add Member Button */}
              <button
                type="button"
                id="add-member-btn"
                onClick={handleAddMember}
                className="px-5 py-2 rounded-[4px] border border-[#1a73e8] text-[#1a73e8] hover:bg-[#f8fafd] font-medium transition-colors flex items-center gap-2 cursor-pointer text-[13.5px]"
              >
                <i className="fa-solid fa-plus text-xs"></i> Add Member
              </button>
            </form>

            {/* Added Members List */}
            {rows.length > 0 && (
              <div className="mb-8 space-y-2">
                <p className="text-xs font-semibold text-[#5f6368] uppercase tracking-wider">
                  Added Corporate Members ({rows.length})
                </p>
                <div className="space-y-2">
                  {rows.map((row, idx) => (
                    <div
                      key={idx}
                      className="p-3.5 rounded-[8px] border border-[#dadce0] bg-[#f8f9fa] flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#1a73e8] text-white flex items-center justify-center font-semibold text-xs">
                          {row.firstName.charAt(0).toUpperCase()}{row.lastName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-[14px] font-medium text-[#202124]">
                            {row.firstName} {row.lastName}
                          </p>
                          <p className="text-xs text-[#5f6368]">
                            {row.selectedOwnership} · {row.ownershipPercentage}% ownership
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemove(idx)}
                        className="text-[#5f6368] hover:text-[#d93025] transition-colors p-1.5 cursor-pointer bg-transparent border-none"
                        title="Remove member"
                      >
                        <i className="fa-solid fa-trash-can text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-6 border-t border-[#f1f3f4]">
              {!isEdit && (
                <button
                  type="button"
                  onClick={() => navigate("/business_type")}
                  className="px-6 py-2.5 rounded-[4px] border border-[#dadce0] text-[#1a73e8] font-medium hover:bg-[#f8f9fa] transition-colors flex items-center gap-2 cursor-pointer text-[14px]"
                >
                  <i className="fa-solid fa-arrow-left text-xs"></i> Back
                </button>
              )}
              <button
                type="button"
                onClick={handleNext}
                className="px-8 py-2.5 rounded-[4px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium shadow-sm transition-all flex items-center gap-2 cursor-pointer text-[14px]"
              >
                {isEdit ? "Save & Return" : "Next"} <i className="fa-solid fa-arrow-right text-xs"></i>
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* Alert Modal */}
      {modal.open && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={closeAlert}
        >
          <div
            className="bg-white rounded-[12px] p-6 max-w-sm w-full shadow-xl text-center space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-full bg-[#fce8e6] text-[#d93025] flex items-center justify-center mx-auto text-xl">
              <i className="fa-solid fa-circle-exclamation"></i>
            </div>
            <p className="text-[14.5px] text-[#202124]">{modal.message}</p>
            <button
              onClick={closeAlert}
              className="w-full py-2.5 rounded-[4px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium text-[14px] cursor-pointer"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
